// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/SellerKpiRow.tsx
import React from 'react';
import type { SellerDashboardResponse } from '@troia-v3/types';

import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';
import { periodInLabel } from '@/shared/utils/gamificationPeriod';

import { formatPts, previousMonthName } from '../lib/sellerFormat';

interface KpiProps {
  icon: IconName; label: string; value: string; purple?: boolean;
  delta?: { value: string; dir: 'up' | 'down' | 'flat' }; caption: string; testId: string;
}

/** Tile do mockup 07 (.kpi): ícone + rótulo em cima, valor, delta + legenda embaixo. */
const Kpi: React.FC<KpiProps> = ({ icon, label, value, purple, delta, caption, testId }) => (
  <div className="rounded-xl px-4 py-[15px] flex flex-col gap-[9px] min-w-0"
    style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }} data-testid={testId}>
    <div className="flex items-center gap-[9px] min-w-0">
      <span className="w-8 h-8 rounded-[9px] flex items-center justify-center flex-none"
        style={purple ? { backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' } : { backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-card-text)' }}>
        <Icon name={icon} size={16} />
      </span>
      <span className="text-xs font-semibold truncate" style={{ color: 'var(--theme-muted-text)' }}>{label}</span>
    </div>
    <div className="text-[26px] font-bold leading-[1.1] tracking-[-.02em]" style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div className="flex items-center gap-2 text-xs min-w-0" style={{ color: 'var(--theme-muted-text)' }}>
      {delta && (
        <span className="inline-flex items-center gap-0.5 font-semibold px-[7px] py-[2px] rounded-full flex-none"
          style={delta.dir === 'up' ? { color: 'var(--theme-success-fg)', background: 'var(--theme-success-subtle)' }
            : delta.dir === 'down' ? { color: 'var(--theme-error-fg)', background: 'var(--theme-error-subtle)' }
            : { color: 'var(--theme-muted-text)', backgroundColor: 'var(--theme-surface-sunken)' }}>
          {delta.dir === 'up' ? '↑' : delta.dir === 'down' ? '↓' : '→'} {delta.value}
        </span>
      )}
      <span className="truncate">{caption}</span>
    </div>
  </div>
);

interface SellerKpiRowProps {
  kpis: SellerDashboardResponse['kpis'];
  ranking: SellerDashboardResponse['ranking'];
  unitName?: string;
  /**
   * Chave do período do seletor. Os KPIs NÃO dependem dele (são sempre de
   * hoje/mês corrente); serve só para achar o mês corrente da legenda "vs.
   * {mês anterior}" — ver `previousMonthName`.
   */
  periodKey: string;
  /**
   * Período da GAMIFICAÇÃO configurado pela empresa — rotula "Pontos {no
   * mês|na semana|no trimestre}". Não é o período do segmented (`periodKey`):
   * `periodPoints` sempre veio do ciclo da gamificação, mas o rótulo dizia
   * "no mês" fixo, mentindo para quem configura semana/trimestre.
   */
  gamificationPeriod: SellerDashboardResponse['gamificationPeriod'];
}

const dirOf = (n: number): 'up' | 'down' | 'flat' => (n > 0 ? 'up' : n < 0 ? 'down' : 'flat');

export const SellerKpiRow: React.FC<SellerKpiRowProps> = ({ kpis, ranking, unitName, periodKey, gamificationPeriod }) => {
  const prevMonth = previousMonthName(periodKey);
  const rankingHidden = !ranking || ranking.visibility === 'only_me';
  const trendDir = ranking?.me?.trend === 'up' ? 'up' : ranking?.me?.trend === 'down' ? 'down' : 'flat';
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Kpi icon="users" label="Leads hoje" value={formatPts(kpis.leadsToday)} testId="seller-kpi-leads-today"
        delta={{ value: String(Math.abs(kpis.leadsTodayDelta)), dir: dirOf(kpis.leadsTodayDelta) }} caption="vs. ontem" />
      <Kpi icon="trending-up" label="Leads no mês" value={formatPts(kpis.leadsMonth)} testId="seller-kpi-leads-month"
        delta={{ value: `${Math.abs(kpis.leadsMonthDeltaPct)}%`, dir: dirOf(kpis.leadsMonthDeltaPct) }} caption={`vs. ${prevMonth}`} />
      {/* `pointsToday` pode ser NEGATIVO (estorno de venda no mesmo dia): seta
          para baixo + valor absoluto, senão a pílula mostrava "↑ -10". */}
      <Kpi icon="star" label={`Pontos ${periodInLabel(gamificationPeriod)}`} value={formatPts(kpis.periodPoints)} purple testId="seller-kpi-points"
        delta={{ value: formatPts(Math.abs(kpis.pointsToday)), dir: dirOf(kpis.pointsToday) }}
        caption={kpis.pointsToday < 0 ? 'estornados hoje' : 'ganhos hoje'} />
      {rankingHidden ? (
        <Kpi icon="award" label="Nível atual" value={kpis.level.name} testId="seller-kpi-level" caption={`${formatPts(kpis.totalPoints)} pts acumulados`} />
      ) : (
        <Kpi icon="award" label="Posição no ranking" value={kpis.position ? `#${kpis.position}` : '—'} testId="seller-kpi-position"
          delta={ranking?.me?.trend && ranking.me.trend !== 'flat' ? { value: '1', dir: trendDir } : undefined}
          caption={`${unitName ? `loja ${unitName} · ` : ''}${kpis.total ?? ranking?.total ?? 0} vendedores`} />
      )}
    </div>
  );
};
SellerKpiRow.displayName = 'SellerKpiRow';
